import React, { useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { AlertTriangle, CheckCircle2, Loader2, Play, ShieldCheck, TrendingUp, Lock } from 'lucide-react';

const fmt = (n) => (Number(n) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const AdminRecomputeDepassements = () => {
  const { hasPermission } = useAuth();
  const [loading, setLoading] = useState(false);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [selected, setSelected] = useState({});
  const [applying, setApplying] = useState(false);
  const [lastApply, setLastApply] = useState(null);

  const runDryRun = async () => {
    setLoading(true);
    setDryRunResult(null);
    setLastApply(null);
    try {
      const { data } = await api.post('/admin/turpe/recompute-depassements', {
        all: true,
        dry_run: true,
      });
      setDryRunResult(data);
      // Pré-sélectionner ceux dont le CMDPS change (diff > 0.01 €)
      const preSel = {};
      (data.diffs || []).forEach(d => {
        const deltaActuel = Math.abs((d.cmdps_actuel?.apres || 0) - (d.cmdps_actuel?.avant || 0));
        const deltaPropose = Math.abs((d.cmdps_propose?.apres || 0) - (d.cmdps_propose?.avant || 0));
        if (deltaActuel > 0.01 || deltaPropose > 0.01) preSel[d.rapport_id] = true;
      });
      setSelected(preSel);
      toast.success(`${data.total_rapports} rapports analysés`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur analyse');
    } finally {
      setLoading(false);
    }
  };

  const toggleAll = (checked) => {
    if (!dryRunResult) return;
    const next = {};
    dryRunResult.diffs.forEach(d => { next[d.rapport_id] = checked; });
    setSelected(next);
  };

  const applyCorrection = async () => {
    const ids = Object.keys(selected).filter(k => selected[k]);
    if (ids.length === 0) {
      toast.error('Aucun rapport sélectionné');
      return;
    }
    if (!window.confirm(`Appliquer la correction des dépassements sur ${ids.length} rapport(s) ? Action irréversible.`)) return;
    setApplying(true);
    try {
      const { data } = await api.post('/admin/turpe/recompute-depassements', {
        rapport_ids: ids,
        dry_run: false,
      });
      setLastApply(data);
      toast.success(`${data.updated} rapport(s) mis à jour`);
      // Retirer les rapports déjà appliqués de la dry-run table pour éviter double-application
      const remaining = (dryRunResult?.diffs || []).filter(d => !ids.includes(d.rapport_id));
      setDryRunResult({ ...dryRunResult, diffs: remaining });
      setSelected({});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur application');
    } finally {
      setApplying(false);
    }
  };

  const diffsImpactes = (dryRunResult?.diffs || []).filter(d => {
    const deltaActuel = Math.abs((d.cmdps_actuel?.apres || 0) - (d.cmdps_actuel?.avant || 0));
    const deltaPropose = Math.abs((d.cmdps_propose?.apres || 0) - (d.cmdps_propose?.avant || 0));
    return deltaActuel > 0.01 || deltaPropose > 0.01;
  });
  const diffsInchanges = (dryRunResult?.diffs || []).filter(d => {
    const deltaActuel = Math.abs((d.cmdps_actuel?.apres || 0) - (d.cmdps_actuel?.avant || 0));
    const deltaPropose = Math.abs((d.cmdps_propose?.apres || 0) - (d.cmdps_propose?.avant || 0));
    return deltaActuel <= 0.01 && deltaPropose <= 0.01;
  });
  const selCount = Object.values(selected).filter(Boolean).length;

  // Permission gate côté frontend (le backend applique déjà le 403, mais UX plus clair)
  if (!hasPermission || !hasPermission('admin_turpe', 'edit')) {
    return (
      <div className="p-8 max-w-2xl mx-auto" data-testid="admin-recompute-denied">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-start gap-3">
          <Lock className="w-6 h-6 text-red-600 flex-shrink-0" />
          <div>
            <h2 className="font-semibold text-red-900">Accès refusé</h2>
            <p className="text-sm text-red-800 mt-1">
              Cette page est réservée aux administrateurs disposant du droit d'édition sur
              les paramètres TURPE (<code>admin_turpe.edit</code>).
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-6" data-testid="admin-recompute-page">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
          <ShieldCheck className="w-6 h-6 text-amber-600" />
          Recalcul des dépassements TURPE (correctif)
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Corrige les rapports TURPE existants impactés par l'ancien bug de calcul des dépassements
          (comparaison globale au lieu de par plage horaire). Le nouveau calcul compare chaque point
          à la puissance souscrite de sa plage (HPH / HCH / HPB / HCB).
        </p>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3" data-testid="notice">
        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-900">
          <p className="font-medium mb-1">Mode sécurisé en deux étapes :</p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Cliquez sur <strong>Analyser (dry-run)</strong> — aucune donnée modifiée, simulation uniquement.</li>
            <li>Vérifiez les diffs puis sélectionnez les rapports à corriger.</li>
            <li>Cliquez sur <strong>Appliquer la correction</strong> — met à jour définitivement la base.</li>
          </ol>
        </div>
      </div>

      <div className="flex gap-3 flex-wrap">
        <button
          type="button"
          onClick={runDryRun}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm"
          data-testid="btn-dry-run"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          {loading ? 'Analyse en cours…' : 'Analyser tous les rapports (dry-run)'}
        </button>
        {dryRunResult && (
          <button
            type="button"
            onClick={applyCorrection}
            disabled={applying || selCount === 0}
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50 text-sm"
            data-testid="btn-apply"
          >
            {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            {applying ? 'Application…' : `Appliquer sur ${selCount} rapport(s)`}
          </button>
        )}
      </div>

      {lastApply && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid="last-apply-result">
          <p className="text-sm text-emerald-900">
            <CheckCircle2 className="inline-block w-4 h-4 mr-1" />
            Dernière exécution : <strong>{lastApply.updated}</strong> rapport(s) corrigés sur {lastApply.total_rapports} analysés.
            {lastApply.errors?.length > 0 && (
              <span className="text-red-700"> — {lastApply.errors.length} erreur(s).</span>
            )}
          </p>
        </div>
      )}

      {dryRunResult && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <p className="text-xs text-gray-500">Rapports analysés</p>
              <p className="text-2xl font-semibold text-gray-900">{dryRunResult.total_rapports}</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <p className="text-xs text-gray-500">Impactés (CMDPS évolue)</p>
              <p className="text-2xl font-semibold text-amber-600">{diffsImpactes.length}</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <p className="text-xs text-gray-500">Inchangés</p>
              <p className="text-2xl font-semibold text-gray-600">{diffsInchanges.length}</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <p className="text-xs text-gray-500">Sans graphData (skippés)</p>
              <p className="text-2xl font-semibold text-gray-400">{dryRunResult.skipped_sans_graph}</p>
            </div>
          </div>

          {diffsImpactes.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="impactes-table">
              <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between gap-2 flex-wrap">
                <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-amber-600" />
                  Rapports impactés par la correction ({diffsImpactes.length})
                </h2>
                <div className="flex gap-2 text-xs">
                  <button
                    type="button"
                    onClick={() => toggleAll(true)}
                    className="px-2 py-1 border border-gray-300 rounded hover:bg-gray-50"
                    data-testid="btn-select-all"
                  >Tout sélectionner</button>
                  <button
                    type="button"
                    onClick={() => toggleAll(false)}
                    className="px-2 py-1 border border-gray-300 rounded hover:bg-gray-50"
                    data-testid="btn-select-none"
                  >Tout désélectionner</button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-600">
                    <tr>
                      <th className="px-3 py-2 text-left w-8"></th>
                      <th className="px-3 py-2 text-left">Client</th>
                      <th className="px-3 py-2 text-left">Formule</th>
                      <th className="px-3 py-2 text-right">Heures dépass.</th>
                      <th className="px-3 py-2 text-right">CMDPS avant</th>
                      <th className="px-3 py-2 text-right">CMDPS après</th>
                      <th className="px-3 py-2 text-right">Économie avant</th>
                      <th className="px-3 py-2 text-right">Économie après</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {diffsImpactes.map(d => (
                      <tr key={d.rapport_id} className={selected[d.rapport_id] ? 'bg-blue-50' : ''} data-testid={`row-${d.rapport_id}`}>
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={!!selected[d.rapport_id]}
                            onChange={e => setSelected({ ...selected, [d.rapport_id]: e.target.checked })}
                            data-testid={`check-${d.rapport_id}`}
                          />
                        </td>
                        <td className="px-3 py-2 font-medium">{d.client || '—'}</td>
                        <td className="px-3 py-2 text-gray-600">{d.formule}</td>
                        <td className="px-3 py-2 text-right font-mono">{d.depassements_actuel_new?.heuresUniques || 0}</td>
                        <td className="px-3 py-2 text-right font-mono text-gray-500">{fmt(d.cmdps_actuel?.avant)} €</td>
                        <td className="px-3 py-2 text-right font-mono text-amber-700 font-semibold">{fmt(d.cmdps_actuel?.apres)} €</td>
                        <td className="px-3 py-2 text-right font-mono text-gray-500">{fmt(d.economie?.avant)} €</td>
                        <td className="px-3 py-2 text-right font-mono text-emerald-700 font-semibold">{fmt(d.economie?.apres)} €</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {diffsImpactes.length === 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-2 text-emerald-800 text-sm" data-testid="no-impact">
              <CheckCircle2 className="w-4 h-4" />
              Aucun rapport impacté. Les calculs de dépassement sont déjà corrects sur l'ensemble du parc.
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminRecomputeDepassements;
